import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import { useOrg } from "../../contexts/OrgContext.tsx";
import {
  RequestTemplateEditor,
  FormTemplateData,
} from "./RequestTemplateEditor";
import { buildFormTemplateData } from "./buildTemplateInitialData";
import { FieldType, RoutingType } from "@secure-caseworker/shared";
import { useToast } from "../../contexts/ToastContext.tsx";

export const EditRequestTemplatePage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const { navigateOrg, navigateOrgUrl, orgId } = useOrg();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialData, setInitialData] = useState<
    FormTemplateData | undefined
  >();

  useEffect(() => {
    if (templateId && orgId) {
      loadTemplate();
    }
  }, [templateId, orgId]);

  const loadTemplate = async () => {
    if (!templateId || !orgId) return;

    setInitialLoading(true);
    try {
      const template = await apiClient.getRequestTemplate(templateId, orgId);
      const formData = await buildFormTemplateData(template);
      setInitialData(formData);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load template",
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (data: FormTemplateData) => {
    if (!templateId || !orgId) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Template ID not found",
      });
      return;
    }

    setLoading(true);
    try {
      await apiClient.updateRequestTemplate(
        templateId,
        {
          title: data.title,
          description: data.description || "",
          ...(data.type === "form" && {
            form_fields: data.fields.map((f, index) => ({
              key: f.id,
              label: f.label,
              field_type: f.field_type as FieldType,
              required: f.required,
              options: f.options ?? undefined,
              order: index,
            })),
          }),
          ...(data.type === "signature" && {
            routing:
              data.routing === "sequential"
                ? RoutingType.SEQUENTIAL
                : RoutingType.PARALLEL,
            signature_signers: data.signerRoles?.map((signer) => ({
              key: signer.key,
              label: signer.label,
              signing_order: signer.signing_order,
              required: signer.required,
            })) ?? [
              {
                key: "signer_1",
                label: "Signer",
                signing_order: null,
                required: true,
              },
            ],
          }),
        },
        orgId
      );

      // Save signature places for existing PDF documents
      if (data.pdfDocuments) {
        for (const doc of data.pdfDocuments) {
          if (doc.id) {
            await apiClient.updateTemplateSignaturePlaces(
              templateId,
              doc.id,
              doc.signaturePlaces,
              orgId
            );
          }
        }
      }

      showToast("Template updated successfully");
      navigateOrg("/request-templates");
    } catch (error) {
      showToast("Failed to update template", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigateOrg("/request-templates");
  };

  if (initialLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading template...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          {
            label: "Request Templates",
            path: navigateOrgUrl("/request-templates"),
          },
          { label: "Edit Template" },
        ]}
      />

      <div className="flex items-center gap-3">
        <FileText size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Edit Request Template
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Update your template
          </p>
        </div>
      </div>

      <RequestTemplateEditor
        initialData={initialData}
        templateId={templateId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel="Update Template"
        isLoading={loading}
        typeDisabled={true}
      />
    </div>
  );
};
